<script lang="ts" setup>
import type { Component } from 'vue';
import type { CompositePrimaryKey, PrimitivePrimaryKey } from '#layers/data-model/public';
import type { RelationMatchPickerProps } from './relation-match-picker';
import { computed, ref, watch } from 'vue';
import { isSameKey } from '#layers/data-model/public';

interface FilterRelatedItemPopoverProps extends RelationMatchPickerProps {
	popover: Component;
	allowCurrentUser?: boolean;
}

const { keys, keyFields, autoOpen, popover, allowCurrentUser = false, disabled = false, readonly = false } = defineProps<FilterRelatedItemPopoverProps>();

const emit = defineEmits<{
	'update:keys': [keys: CompositePrimaryKey[]];
}>();

const isEditable = computed(() => !disabled && !readonly);

const open = ref(false);

watch(() => autoOpen, (token) => {
	if (token && isEditable.value) open.value = true;
}, { immediate: true });

// An inline picker selects an item by the one field its keys carry.
function keyOf(id: PrimitivePrimaryKey): CompositePrimaryKey {
	const [field, ...rest] = keyFields;
	if (!field || rest.length > 0) throw new Error('[FilterRelatedItemPopover] an inline picker needs exactly one key field');
	return { [field.apiName]: id };
}

function isSelected(item: { id: PrimitivePrimaryKey }): boolean {
	return keys.some((candidate) => isSameKey(candidate, keyOf(item.id)));
}

function onSelect(id: PrimitivePrimaryKey) {
	const key = keyOf(id);
	const isConnected = keys.some((candidate) => isSameKey(candidate, key));

	emit('update:keys', isConnected ? keys.filter((candidate) => !isSameKey(candidate, key)) : [...keys, key]);
}
</script>

<template>
	<component
		:is="popover"
		v-if="isEditable"
		v-model:open="open"
		multiple
		:is-selected
		:allow-current-user
		@select="onSelect"
	>
		<div class="flex h-full cursor-pointer items-center px-1">
			<slot />
		</div>
	</component>
	<div v-else class="flex h-full items-center px-1">
		<slot />
	</div>
</template>
